import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const textClass = scrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-white/85 hover:text-white";

  const brandClass = scrolled
    ? "text-foreground-900"
    : "text-white";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-4">
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl tracking-wide whitespace-nowrap cursor-pointer ${brandClass}`}
          onClick={() => window.scrollTo(0, 0)}
        >
          Pandabuy<span className="font-light opacity-70">Sheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            onClick={() => window.scrollTo(0, 0)}
          >
            Home
          </Link>
          {isHome ? (
            <a
              href="#categories"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            >
              Categories
            </a>
          ) : (
            <Link
              to="/#categories"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            >
              Categories
            </Link>
          )}
          {isHome ? (
            <a
              href="#features"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            >
              Features
            </a>
          ) : (
            <Link
              to="/#features"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            >
              Features
            </Link>
          )}

          <div className="relative">
            <button
              onClick={() => setGuideOpen(!guideOpen)}
              className={`flex items-center gap-1 text-sm whitespace-nowrap cursor-pointer transition-colors ${textClass}`}
            >
              Shopping Guide
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? "rotate-180" : ""}`}></i>
            </button>
            {guideOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 w-64 max-h-72 overflow-y-auto z-50">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 whitespace-nowrap cursor-pointer transition-colors"
                    onClick={() => { setGuideOpen(false); window.scrollTo(0, 0); }}
                  >
                    {article.title.length > 45 ? article.title.slice(0, 45) + "..." : article.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-1 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            Buy It Now
          </a>
        </div>

        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
            scrolled ? "text-foreground-900" : "text-white"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-6 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            className="text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer border-b border-background-100"
            onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
          >
            <i className="ri-home-4-line mr-2"></i>Home
          </Link>
          {isHome ? (
            <a
              href="#categories"
              className="text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer border-b border-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-grid-line mr-2"></i>Categories
            </a>
          ) : (
            <Link
              to="/#categories"
              className="text-sm text-foreground-700 hover:text-foreground-950 py-2.5 cursor-pointer border-b border-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-grid-line mr-2"></i>Categories
            </Link>
          )}

          <div className="py-2.5 border-b border-background-100">
            <p className="text-xs text-foreground-400 font-medium uppercase tracking-wide mb-2">Browse by Category</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-xs text-foreground-600 hover:text-foreground-900 py-1.5 whitespace-nowrap cursor-pointer transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="py-2.5 border-b border-background-100">
            <p className="text-xs text-foreground-400 font-medium uppercase tracking-wide mb-2">Shopping Guides</p>
            <div className="flex flex-col gap-1">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="text-xs text-foreground-600 hover:text-foreground-900 py-1.5 whitespace-nowrap cursor-pointer transition-colors"
                  onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                >
                  {article.tag}: {article.title.length > 35 ? article.title.slice(0, 35) + "..." : article.title}
                </Link>
              ))}
            </div>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="mt-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors inline-block w-fit"
            onClick={() => setMobileOpen(false)}
          >
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}